'use client'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Product } from '@/payload-types'

export interface CartItem {
  id: string
  product: Product
  quantity: number
  addedAt: Date
}

interface CartStore {
  items: CartItem[]
  isOpen: boolean
  
  // Actions
  addItem: (product: Product, quantity?: number) => void
  removeItem: (productId: string) => void
  updateQuantity: (productId: string, quantity: number) => void
  clearCart: () => void
  openCart: () => void
  closeCart: () => void
  toggleCart: () => void
  
  // Computed values
  totalItems: () => number
  totalPrice: () => number
  getItem: (productId: string) => CartItem | undefined
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: (product: Product, quantity = 1) => {
        const state = get()
        const existingItem = state.items.find(item => item.product.id === product.id)
        
        // Check basic constraints first
        let finalQuantity = quantity
        
        // Apply maxPerUser constraint
        if (product.maxPerUser) {
          finalQuantity = Math.min(finalQuantity, product.maxPerUser)
        }
        
        // Apply stock constraint if finite stock
        if (product.hasFiniteStock && product.stock !== undefined && product.stock !== null) {
          finalQuantity = Math.min(finalQuantity, product.stock)
        }
        
        if (existingItem) {
          // Update quantity if item already exists
          const newQuantity = existingItem.quantity + finalQuantity
          
          // Re-apply constraints to total quantity
          let constrainedQuantity = newQuantity
          if (product.maxPerUser) {
            constrainedQuantity = Math.min(constrainedQuantity, product.maxPerUser)
          }
          if (product.hasFiniteStock && product.stock !== undefined && product.stock !== null) {
            constrainedQuantity = Math.min(constrainedQuantity, product.stock)
          }
          
          set({
            items: state.items.map(item =>
              item.product.id === product.id
                ? { ...item, quantity: constrainedQuantity }
                : item
            )
          })
        } else {
          // Add new item
          const newItem: CartItem = {
            id: `${product.id}-${Date.now()}`,
            product,
            quantity: finalQuantity,
            addedAt: new Date()
          }
          
          set({
            items: [...state.items, newItem]
          })
        }
      },

      removeItem: (productId: string) => {
        set({
          items: get().items.filter(item => item.product.id !== productId)
        })
      },

      updateQuantity: (productId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(productId)
          return
        }

        set({
          items: get().items.map(item => {
            if (item.product.id === productId) {
              let finalQuantity = quantity
              
              // Apply maxPerUser constraint
              if (item.product.maxPerUser) {
                finalQuantity = Math.min(finalQuantity, item.product.maxPerUser)
              }
              
              // Apply stock constraint if finite stock
              if (item.product.hasFiniteStock && item.product.stock !== undefined && item.product.stock !== null) {
                finalQuantity = Math.min(finalQuantity, item.product.stock)
              }
              
              return { ...item, quantity: finalQuantity }
            }
            return item
          })
        })
      },

      clearCart: () => {
        set({ items: [] })
      },

      openCart: () => {
        set({ isOpen: true })
      },

      closeCart: () => {
        set({ isOpen: false })
      },

      toggleCart: () => {
        set({ isOpen: !get().isOpen })
      },

      totalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0)
      },

      totalPrice: () => {
        return get().items.reduce((total, item) => {
          return total + (item.product.price * item.quantity)
        }, 0)
      },

      getItem: (productId: string) => {
        return get().items.find(item => item.product.id === productId)
      }
    }),
    {
      name: 'jeugdfilms-cart',
      partialize: (state) => ({ items: state.items }), // Only persist items, not UI state
    }
  )
)